import { useMutation } from '@apollo/client';
import { ConfirmationDialogContent } from '@openx/components-core/lib/Dialog/ConfirmationDialog/components/ConfirmationDialogContent.tsx';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';

import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { ArchiveBidderCreativeDocument } from '../../../../graphql/mutation/creatives/ArchiveBidderCreative.generated.tsx';
import { CreateLogDocument } from '../../../../graphql/mutation/error/createLog.generated.tsx';

import type { CreativeRow } from '../types.ts';

type OpenDeleteModalParams = {
  creative: CreativeRow;
  onSuccess?: () => void;
};

export const useDeleteCreative = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [pushLog] = useMutation(CreateLogDocument);
  const [archiveCreative, { loading: deleteLoading }] = useMutation(ArchiveBidderCreativeDocument);
  const {
    open: confirmationOpen,
    close: confirmationClose,
    setLoading: confirmationSetLoading,
  } = useConfirmationDialog();

  const openDeleteModal = useCallback(
    ({ creative, onSuccess }: OpenDeleteModalParams) => {
      const onConfirmed = async () => {
        try {
          confirmationSetLoading(true);

          await archiveCreative({
            variables: { id: creative.id },
            update: cache => {
              cache.evict({ fieldName: 'bidderCreatives' });
              cache.gc();
            },
          });

          enqueueSnackbar(t('Creative successfully deleted'), { variant: 'success' });
          onSuccess?.();
        } catch (_error) {
          const location = window.location.href;

          await pushLog({
            variables: {
              location,
              msg: 'Failed to delete creative',
              name: currentUser?.displayName,
              email: currentUser?.email,
            },
          });

          enqueueSnackbar(t('Failed to delete creative'), { variant: 'error' });
        } finally {
          confirmationSetLoading(false);
          confirmationClose();
        }
      };

      confirmationOpen({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Delete'),
        content: <ConfirmationDialogContent actionText={t('delete')} name={creative.name} objectText={t('creative')} />,
        customContent: true,
        onConfirmed,
        title: t('Delete Creative'),
      });
    },
    [
      archiveCreative,
      confirmationOpen,
      confirmationClose,
      confirmationSetLoading,
      enqueueSnackbar,
      pushLog,
      t,
      currentUser,
    ]
  );

  return { isLoading: deleteLoading, openDeleteModal };
};
